import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { rupiah, num } from '../lib/format';
import { Plus, Pencil, Trash, ShoppingCart, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SATUAN_OPTIONS = ['gram', 'kg', 'ml', 'liter', 'butir', 'pcs', 'pack'];

const statusColor = {
  Aman: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Menipis: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Habis: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const emptyForm = { nama: '', satuan: 'gram', harga_beli: '', isi_kemasan: '', stok: '', stok_min: '' };

export default function BahanBaku() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [buyFor, setBuyFor] = useState(null);
  const [buyForm, setBuyForm] = useState({ qty_kemasan: '', harga_beli_baru: '', isi_kemasan_baru: '', catatan: '' });
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const r = await api.get('/materials');
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      nama: m.nama, satuan: m.satuan,
      harga_beli: m.harga_beli, isi_kemasan: m.isi_kemasan,
      stok: m.stok, stok_min: m.stok_min,
    });
    setShowForm(true);
  };

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!(parseFloat(form.isi_kemasan) > 0)) { toast.error('Isi per kemasan harus lebih dari 0'); return; }
    const payload = {
      ...form,
      harga_beli: parseFloat(form.harga_beli) || 0,
      isi_kemasan: parseFloat(form.isi_kemasan) || 0,
      stok: parseFloat(form.stok) || 0,
      stok_min: parseFloat(form.stok_min) || 0,
    };
    try {
      if (editId) await api.patch(`/materials/${editId}`, payload);
      else await api.post('/materials', payload);
      toast.success(editId ? 'Bahan diperbarui' : 'Bahan ditambahkan');
      resetForm();
      load();
    } catch (e) {
      toast.error('Gagal menyimpan bahan');
    }
  };

  const del = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); setTimeout(() => setConfirmDel(null), 3000); return; }
    await api.delete(`/materials/${id}`);
    toast.success('Bahan dihapus');
    setConfirmDel(null);
    load();
  };

  const submitBuy = async (e) => {
    e.preventDefault();
    const payload = {
      material_id: buyFor.id,
      qty_kemasan: parseFloat(buyForm.qty_kemasan) || 0,
      harga_beli_baru: buyForm.harga_beli_baru ? parseFloat(buyForm.harga_beli_baru) : null,
      isi_kemasan_baru: buyForm.isi_kemasan_baru ? parseFloat(buyForm.isi_kemasan_baru) : null,
      catatan: buyForm.catatan || null,
    };
    try {
      await api.post('/purchases', payload);
      toast.success(`Pembelian ${buyFor.nama} dicatat`);
      setBuyFor(null);
      setBuyForm({ qty_kemasan: '', harga_beli_baru: '', isi_kemasan_baru: '', catatan: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mencatat pembelian');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <p className="text-muted-foreground max-w-lg text-sm">Kelola bahan baku, harga per satuan dihitung otomatis dari harga beli dibagi isi kemasan.</p>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          data-testid="btn-add-material"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90"
        >
          <Plus size={18} weight="bold" /> Tambah Bahan
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="form-material" className="bg-card border border-border/60 rounded-xl p-5 lg:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">{editId ? 'Edit Bahan' : 'Tambah Bahan'}</h3>
            <button type="button" onClick={resetForm}><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Nama Bahan" value={form.nama} onChange={(v) => setForm({ ...form, nama: v })} required testid="mat-nama" />
            <SelectField label="Satuan" value={form.satuan} options={SATUAN_OPTIONS} onChange={(v) => setForm({ ...form, satuan: v })} testid="mat-satuan" />
            <Field label="Harga Beli (per kemasan)" type="number" value={form.harga_beli} onChange={(v) => setForm({ ...form, harga_beli: v })} required testid="mat-harga" />
            <Field label="Isi per Kemasan" type="number" value={form.isi_kemasan} onChange={(v) => setForm({ ...form, isi_kemasan: v })} required testid="mat-isi" />
            <Field label="Stok Saat Ini" type="number" value={form.stok} onChange={(v) => setForm({ ...form, stok: v })} testid="mat-stok" />
            <Field label="Batas Stok Minimum" type="number" value={form.stok_min} onChange={(v) => setForm({ ...form, stok_min: v })} testid="mat-stokmin" />
          </div>
          <div className="text-sm text-muted-foreground">
            Harga per satuan: <span className="font-semibold text-foreground">
              {rupiah((parseFloat(form.harga_beli) || 0) / (parseFloat(form.isi_kemasan) || 1))}
            </span> / {form.satuan}
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="btn-submit-material" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90">
              {editId ? 'Simpan Perubahan' : 'Tambah Bahan'}
            </button>
            <button type="button" onClick={resetForm} className="px-5 py-2.5 border border-border rounded-lg font-medium hover:bg-secondary">Batal</button>
          </div>
        </form>
      )}

      {buyFor && (
        <form onSubmit={submitBuy} data-testid="form-purchase" className="bg-card border border-primary/40 rounded-xl p-5 lg:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Catat Pembelian: {buyFor.nama}</h3>
            <button type="button" onClick={() => setBuyFor(null)}><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Jumlah Kemasan Dibeli" type="number" value={buyForm.qty_kemasan} onChange={(v) => setBuyForm({ ...buyForm, qty_kemasan: v })} required testid="buy-qty" />
            <Field label={`Harga Beli Baru (opsional, saat ini ${rupiah(buyFor.harga_beli)})`} type="number" value={buyForm.harga_beli_baru} onChange={(v) => setBuyForm({ ...buyForm, harga_beli_baru: v })} testid="buy-harga" />
            <Field label={`Isi Kemasan Baru (opsional, saat ini ${buyFor.isi_kemasan})`} type="number" value={buyForm.isi_kemasan_baru} onChange={(v) => setBuyForm({ ...buyForm, isi_kemasan_baru: v })} testid="buy-isi" />
            <Field label="Catatan" value={buyForm.catatan} onChange={(v) => setBuyForm({ ...buyForm, catatan: v })} testid="buy-catatan" />
          </div>
          <button type="submit" data-testid="btn-submit-purchase" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium">Simpan Pembelian</button>
        </form>
      )}

      <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-materials">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="p-3 font-semibold">Nama</th>
                <th className="p-3 font-semibold">Satuan</th>
                <th className="p-3 font-semibold text-right">Harga/Satuan</th>
                <th className="p-3 font-semibold text-right">Stok</th>
                <th className="p-3 font-semibold text-right">Nilai Stok</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-right sticky right-0 bg-card">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">Belum ada bahan. Klik "Tambah Bahan" atau muat contoh data di Dashboard.</td></tr>
              )}
              {items.map((m) => (
                <tr key={m.id} className="border-t border-border/60">
                  <td className="p-3">
                    <div className="font-medium">{m.nama}</div>
                    {m.is_sample && <span className="text-xs text-muted-foreground">contoh</span>}
                  </td>
                  <td className="p-3 text-muted-foreground">{m.satuan}</td>
                  <td className="p-3 text-right tabular-nums">{rupiah(m.harga_per_satuan)}</td>
                  <td className="p-3 text-right tabular-nums">{num(m.stok, 2)} <span className="text-muted-foreground text-xs">(min {num(m.stok_min)})</span></td>
                  <td className="p-3 text-right tabular-nums font-medium">{rupiah(m.nilai_stok)}</td>
                  <td className="p-3"><span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor[m.status]}`}>{m.status}</span></td>
                  <td className="p-3 sticky right-0 bg-card">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setBuyFor(m)} data-testid={`btn-buy-${m.id}`} className="p-2 rounded-lg hover:bg-secondary text-primary" title="Catat pembelian"><ShoppingCart size={18} /></button>
                      <button onClick={() => openEdit(m)} data-testid={`btn-edit-${m.id}`} className="p-2 rounded-lg hover:bg-secondary" title="Edit"><Pencil size={18} /></button>
                      <button onClick={() => del(m.id)} data-testid={`btn-del-${m.id}`} className={`p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 ${confirmDel === m.id ? 'text-rose-600 bg-rose-100 dark:bg-rose-900/30' : 'text-rose-500'}`} title={confirmDel === m.id ? 'Klik lagi untuk konfirmasi' : 'Hapus'}><Trash size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        step="any"
        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </label>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
